/**
 * The review page for ONE reseller application —
 * `/crm/administration/reseller-applications/:id` (spec E §5.2 + §11.1).
 * The reviewer emails link here, so the path is fixed; the pre-2026-09-12
 * address `/admin/reseller-applications/:id` redirects here (App.tsx).
 * Route-guarded on `reseller_applications:access` (the default export).
 *
 * What the reviewer does here, in order: reads the application as typed;
 * looks at the matcher's ranked candidates; views/downloads the two signed
 * PDFs; uploads the two countersigned copies; then makes the identity
 * decision — Activate as a NEW reseller, Activate as THIS existing one, or
 * Reject. The decision is ONE crm-api transaction (`activate!`), and this
 * page only carries it there and reports what came back.
 *
 * "The system suggests, a human links, and nothing is linked automatically"
 * (spec §5) — every candidate row is a suggestion, and every verb below is
 * a click the reviewer makes.
 */
import { Box, Button, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  RESELLER_APPLICATION_STATUS_LABELS,
  activationBlocker,
  applicantName,
  isOpenApplication,
  type ActivateResellerApplicationInput,
  type ActivateResellerApplicationResponse,
  type CountersignDocumentKind,
  type ResellerApplication,
  type ResellerMatchCandidate,
} from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";
import { Link as RouterLink, Navigate, useNavigate, useParams } from "react-router-dom";

import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { useCan } from "@/auth/permissions";
import { toast } from "@/components/ui/use-toast";
import { ApiError } from "@/lib/api";
import { PagePermissionGuard } from "@crm-src/components/page-permission-guard";
import { ActivateAsNewCard } from "./ActivateAsNewCard.js";
import { ApplicationSummaryCard } from "./ApplicationSummaryCard.js";
import { DocumentsCard } from "./DocumentsCard.js";
import { formatDateTime } from "./format.js";
import { LinkResellerDialog } from "./LinkResellerDialog.js";
import { MatchCandidatesCard } from "./MatchCandidatesCard.js";
import { RejectDialog } from "./RejectDialog.js";
import { useResellerApplication, useResellerApplicationMutations } from "./useResellerApplications.js";

/** Where "Back" goes: the Reseller Applications tab of Reseller Management. */
export const APPLICATIONS_TAB = "/crm/administration/resellers?tab=applications";

export function ResellerApplicationDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const appId = /^\d+$/.test(id ?? "") ? Number(id) : null;
  const navigate = useNavigate();
  // `crm:resellers` gates the route; `crm:edit` separates "may look" from
  // "may decide", exactly as crm-api's `require_crm_edit!` enforces it.
  const canManage = useCan("crm:edit");
  const query = useResellerApplication(appId);
  const { countersign, activate, reject } = useResellerApplicationMutations(appId ?? 0);

  if (appId === null) return <Navigate to={APPLICATIONS_TAB} replace />;

  const handleUpload = async (files: Partial<Record<CountersignDocumentKind, File>>): Promise<void> => {
    try {
      await countersign.mutateAsync(files);
      toast({ title: "Countersigned document saved", severity: "success" });
    } catch (e) {
      toast({
        title: "Couldn't upload the document",
        description: e instanceof ApiError ? e.message : "The CRM was unreachable.",
        severity: "error",
      });
      throw e;
    }
  };

  const handleActivate = async (input: ActivateResellerApplicationInput): Promise<ActivateResellerApplicationResponse> => {
    const result = await activate.mutateAsync(input);
    toast({
      title: "Application activated",
      description: `${result.reseller.name}${result.reseller.code ? ` (${result.reseller.code})` : ""} — ${result.membership.email} is now its ${result.membership.role}. The applicant's portal opens on their next poll.`,
      severity: "success",
    });
    return result;
  };

  const handleReject = async (note: string): Promise<void> => {
    await reject.mutateAsync({ note });
    toast({ title: "Application rejected", description: "The applicant has been emailed your note.", severity: "info" });
  };

  return (
    <Box data-cy="reseller-application-detail">
      <BackButton label="Back to applications" onClick={() => navigate(APPLICATIONS_TAB)} sx={{ mb: 2 }} />

      {query.isLoading && <PageLoader />}

      {query.isError && (
        <Box role="alert" sx={{ borderRadius: 1, border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.4), bgcolor: (t) => alpha(t.palette.error.main, 0.12), px: 1.5, py: 1.5, fontSize: 14, color: "error.main" }}>
          <Box sx={{ fontWeight: 600 }}>Couldn&rsquo;t load this application.</Box>
          <Box sx={{ fontSize: 12 }}>{query.error instanceof ApiError ? query.error.message : "Unknown error."}</Box>
          <Button size="small" variant="text" color="inherit" onClick={() => void query.refetch()}>Retry</Button>
        </Box>
      )}

      {query.data && (
        <ReviewBody
          application={query.data.application}
          canManage={canManage}
          isUploading={countersign.isPending}
          onUpload={handleUpload}
          onActivate={handleActivate}
          activateError={activate.isError && activate.error instanceof ApiError ? activate.error : null}
          onActivateErrorReset={() => activate.reset()}
          isActivating={activate.isPending}
          onReject={handleReject}
          rejectError={reject.isError && reject.error instanceof ApiError ? reject.error : null}
          isRejecting={reject.isPending}
        />
      )}
    </Box>
  );
}

function ReviewBody({
  application,
  canManage,
  isUploading,
  onUpload,
  onActivate,
  activateError,
  onActivateErrorReset,
  isActivating,
  onReject,
  rejectError,
  isRejecting,
}: {
  application: ResellerApplication;
  canManage: boolean;
  isUploading: boolean;
  onUpload: (files: Partial<Record<CountersignDocumentKind, File>>) => Promise<void>;
  onActivate: (input: ActivateResellerApplicationInput) => Promise<ActivateResellerApplicationResponse>;
  activateError: ApiError | null;
  onActivateErrorReset: () => void;
  isActivating: boolean;
  onReject: (note: string) => Promise<void>;
  rejectError: ApiError | null;
  isRejecting: boolean;
}): JSX.Element {
  const open = isOpenApplication(application);
  const blocker = activationBlocker(application);
  const [linkTarget, setLinkTarget] = useState<ResellerMatchCandidate | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  // The link dialog and the "activate as new" card share one mutation
  // (`activate`), so a stale error from one verb bleeds into the other's
  // card unless it's cleared on every linkTarget transition — both opening
  // the dialog and cancelling out of it.
  const setLinkTargetAndResetError = (candidate: ResellerMatchCandidate | null): void => {
    onActivateErrorReset();
    setLinkTarget(candidate);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>{application.company_name}</Typography>
            <Chip
              size="small"
              label={RESELLER_APPLICATION_STATUS_LABELS[application.status]}
              color={application.status === "active" ? "success" : application.status === "rejected" ? "default" : "info"}
              data-cy="application-status-chip"
            />
          </Box>
          <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
            {applicantName(application)} · {application.email}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {canManage && open && (
            <Button variant="outlined" color="error" onClick={() => setRejectOpen(true)} data-cy="application-reject">
              Reject
            </Button>
          )}
        </Box>
      </Box>

      {!open && (
        <Box
          data-testid="application-decided"
          data-cy="application-decided"
          role="status"
          sx={{ mb: 3, p: 2, borderRadius: 1, border: 1, borderColor: (t) => alpha(t.palette.info.main, 0.4), bgcolor: (t) => alpha(t.palette.info.main, 0.08), fontSize: 14 }}
        >
          {application.status === "active" && application.reseller ? (
            <>
              Activated — {application.decision === "created" ? "created as" : "linked to"}{" "}
              <Box component={RouterLink} to={`/crm/administration/resellers/${application.reseller.id}`} sx={{ color: "primary.main", fontWeight: 600 }}>
                {application.reseller.name}
                {application.reseller.code ? ` (${application.reseller.code})` : ""}
              </Box>
            </>
          ) : (
            <>Rejected{application.decision_note ? ` — ${application.decision_note}` : ""}</>
          )}
          {application.decided_by && (
            <Box component="span" sx={{ color: "text.secondary" }}>
              {" "}by {application.decided_by.full_name} on {formatDateTime(application.decided_at)}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { lg: "3fr 2fr" }, alignItems: "start" }}>
        <Box sx={{ display: "grid", gap: 3, minWidth: 0 }}>
          <ApplicationSummaryCard application={application} />
          <MatchCandidatesCard
            candidates={application.match_candidates ?? []}
            canManage={canManage && open}
            blocker={blocker}
            onLink={setLinkTargetAndResetError}
          />
          {open && (
            <ActivateAsNewCard
              application={application}
              canManage={canManage}
              blocker={blocker}
              isSubmitting={isActivating}
              error={linkTarget ? null : activateError}
              onActivate={async ({ name, code }) => {
                await onActivate({ mode: "create", name, code });
              }}
            />
          )}
        </Box>
        <DocumentsCard application={application} canManage={canManage} isUploading={isUploading} onUpload={onUpload} />
      </Box>
      <LinkResellerDialog
        key={linkTarget?.reseller_id ?? "none"}
        candidate={linkTarget}
        applicantEmail={application.email}
        isSubmitting={isActivating}
        error={activateError}
        onClose={() => setLinkTargetAndResetError(null)}
        onConfirm={async (role) => {
          if (!linkTarget) return;
          await onActivate({ mode: "link", reseller_id: linkTarget.reseller_id, role });
        }}
      />
      <RejectDialog
        isOpen={rejectOpen}
        companyName={application.company_name}
        isSubmitting={isRejecting}
        error={rejectError}
        onClose={() => setRejectOpen(false)}
        onConfirm={onReject}
      />
    </>
  );
}

/** The routed page: the review surface behind the same page-permission guard
 * every CRM page uses. `reseller_applications:access` is the dashboard's
 * `crm:reseller_applications` grant (super_admin baseline) — NOT the reseller
 * wall — so a sales manager who reaches the Resellers page gets the guard's
 * Access Denied surface here, naming the permission. */
export default function ResellerApplicationReviewPage(): JSX.Element {
  return (
    <PagePermissionGuard requiredPermission="reseller_applications:access" redirectTo="/crm/administration/resellers">
      <ResellerApplicationDetailPage />
    </PagePermissionGuard>
  );
}
